import * as React from "react";
import { observer } from "mobx-react";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { alpha, styled } from "@mui/material/styles";
import { ArrowForwardIosSharpIcon } from "../../icons";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.05)
      : alpha(theme.palette.common.black, 0.03),
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid " + theme.palette.divider,
}));

export type GroupContainerProps = {
  title: string;
  description?: string;
  errorText?: string;
  disabled?: boolean;
  children?: React.ReactElement | React.ReactElement[];
  id?: string;
  expanded?: boolean;
  toggleExpand?: () => void;
  defaultExpanded?: boolean;
  collapsible?: boolean;
};

export const GroupContainer: React.FC<GroupContainerProps> = observer(
  ({
    id,
    disabled,
    title,
    errorText,
    description,
    children,
    expanded,
    toggleExpand,
    defaultExpanded = true,
    collapsible = true,
  }) => {
    // Controlled when a parent supplies both expanded + toggleExpand (the form
    // ViewStore card-state pattern). Otherwise self-manage so the accordion is
    // actually collapsible — callers (e.g. snapshot views) can omit both props.
    const isControlled = expanded !== undefined && toggleExpand !== undefined;
    const [internalExpanded, setInternalExpanded] =
      React.useState(defaultExpanded);
    // Un grupo no colapsable (p.ej. el único grupo de un formulario) queda siempre
    // abierto, sin chevron ni toggle.
    const actualExpanded = collapsible
      ? isControlled
        ? expanded
        : internalExpanded
      : true;
    const handleChange = () => {
      if (!collapsible) return;
      if (isControlled) {
        toggleExpand!();
      } else {
        setInternalExpanded((v) => !v);
      }
    };
    return (
      <Accordion
        id={id}
        expanded={actualExpanded}
        onChange={handleChange}
        disabled={disabled}
      >
        <AccordionSummary
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          expandIcon={
            collapsible ? (
              <ArrowForwardIosSharpIcon sx={{ fontSize: "0.8rem" }} />
            ) : null
          }
          sx={{ pl: 0.5, ...(collapsible ? {} : { cursor: "default" }) }}
        >
          <Stack spacing={0}>
            <Typography sx={{ fontWeight: "bold" }}>{title}</Typography>
            {description && (
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontStyle: "italic",
                  whiteSpace: "pre-line",
                }}
                color="textSecondary"
              >
                {description}
              </Typography>
            )}
            {!actualExpanded && (
              <Typography sx={{ fontSize: "0.75rem" }} color="error">
                {errorText}
              </Typography>
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails style={{ padding: 0 }}>{children}</AccordionDetails>
      </Accordion>
    );
  }
);
